import IconButton from '@/global/components/Button/IconButton'
import ExportarCsv from '@/global/components/DataGrid/components/CustomGridToolBar/ExportarCsv'
import ImprimirButton from '@/global/components/DataGrid/components/CustomGridToolBar/ImprimirButton'
import { GridMenuButton } from '@/global/components/DataGrid/components/CustomGridToolBar/MenuComponents'
import { FooterAviso } from '@/global/components/DataGrid/components/DataTable/Footer'
import { optionsColumn } from '@/global/components/DataGrid/hooks/useDataGridPro'
import useDefaultFilterModel from '@/global/components/DataGrid/hooks/useDefaultFilterModel'
import useDefaultSelectionModel from '@/global/components/DataGrid/hooks/useDefaultSelectionModel'
import useDefaultSortModel from '@/global/components/DataGrid/hooks/useDefaultSortModel'
import FilterModelPro from '@/global/components/DataGrid/utils/filterModelPro'
import {
    GridColDefPro,
    GridSelectionModel,
} from '@/global/components/DataGrid/utils/types'
import { Button } from 'global/components/Button'
import { Link } from 'global/components/Link'
import SelectCompetencia, {
    useCompetencia,
} from 'global/components/Selects/SelectCompetencia'
import ContentGuide from 'global/guides/ContentGuide'
import { hasPermission } from 'hooks/useBackendMenu'
import { ListTodoIcon, PencilIcon, SettingsIcon } from 'lucide-react'
import { useState } from 'react'
import { Outlet, useOutletContext } from 'react-router-dom'
import { type PagamentoHoras, SaldoHoras, pagamentoHoras } from 'service/api'
import { useStore } from 'store'

const columns: GridColDefPro<PagamentoHoras>[] = [
    {
        field: 'nomeFuncionario',
        headerName: 'Funcionário',
        flex: 1,
    },
    {
        field: 'horas',
        headerName: 'H. extras pagas',
        width: 120,
    },
    {
        field: 'faltas',
        headerName: 'Faltas pagas',
        width: 120,
    },
    {
        field: 'saldoExtras',
        headerName: 'Horas extras',
        width: 120,
    },
    {
        field: 'saldoFaltas',
        headerName: 'Faltas',
        width: 120,
    },
    {
        ...optionsColumn,
        field: '__editar',
        renderCell: ({ row }) =>
            hasPermission(['update', 'PagamentoDeHoras'], () => (
                <IconButton href={`editar/${row.funcionarioId}`} title="Editar">
                    <PencilIcon />
                </IconButton>
            )),
    },
]

export default function PagamentoHoras() {
    const [isImprimindo, setIsImprimindo] = useState(false)
    const [selecionados, setSelecionados] = useDefaultSelectionModel()
    const [filterModel, setFilterModel] = useDefaultFilterModel()
    const [competencia, setCompetencia] = useCompetencia()
    const empresa = useStore(state => state.company)

    const { data, isLoading } = pagamentoHoras.useFetch({
        queryParams: {
            empresaId: empresa?.id,
            competencia,
        },
        enabled: !!empresa,
    })

    const rows = data?.map(({ pagamento, saldo }) => ({
        ...pagamento,
        id: pagamento.funcionarioId,
        nomeFuncionario: pagamento.Funcionario.nome,
        codigoEmpresa: empresa?.codEmpresaFolha ?? empresa?.id,
        matriculaFuncionario: pagamento.Funcionario.matricula,
        saldoExtras: saldo?.horasExtras,
        saldoFaltas: saldo?.faltas,
    }))
    const [sortModel, setSortModel, sort] = useDefaultSortModel(columns)

    async function handleImprimir() {
        setIsImprimindo(true)
        return pagamentoHoras
            .pdf({
                data: sort(
                    rows?.filter(
                        new FilterModelPro(filterModel, columns).filter,
                    ),
                ).map(({ id }) => id),
                queryParam: {
                    empresaId: empresa?.id,
                    competencia,
                },
            })
            .finally(() => setIsImprimindo(false))
    }

    return (
        <ContentGuide>
            <ContentGuide.Card>
                <ContentGuide.Header
                    label={['Manutenção do ponto', 'Pagamento de Horas']}
                >
                    <ContentGuide.Filtros SelectEmpresas>
                        <SelectCompetencia
                            onChange={setCompetencia}
                            value={competencia}
                        />
                    </ContentGuide.Filtros>
                </ContentGuide.Header>
                <ContentGuide.DataGrid
                    loading={isLoading}
                    onAdd={
                        <>
                            {hasPermission(
                                ['update', 'PagamentoDeHoras'],
                                () => (
                                    <Button
                                        href="adicionar"
                                        startIcon={<ListTodoIcon />}
                                        disabled={!selecionados.length}
                                    >
                                        Pagar selecionados
                                    </Button>
                                ),
                            )}
                            <ImprimirButton
                                handleClick={handleImprimir}
                                isImprimindo={isImprimindo}
                            />
                        </>
                    }
                    onSelectionModelChange={setSelecionados}
                    checkboxSelection={hasPermission([
                        'update',
                        'PagamentoDeHoras',
                    ])}
                    selectionModel={selecionados}
                    filterModel={filterModel}
                    sortModel={sortModel}
                    onSortModelChange={setSortModel}
                    onFilterModelChange={setFilterModel}
                    rows={rows}
                    columns={columns}
                    ToolbarProps={{
                        hideExportButton: true,
                    }}
                    components={{
                        Toolbar,
                        Footer: () => (
                            <FooterAviso>
                                Lembre-se: Antes de realizar o pagamento de
                                horas, é necessário realizar o{' '}
                                {hasPermission(
                                    ['read', 'CálculoDoPonto'],
                                    () => (
                                        <Link href="/manutencao-ponto/calculo-ponto">
                                            cálculo do ponto
                                        </Link>
                                    ),
                                    () => 'cálculo do ponto',
                                )}{' '}
                                do funcionário para obter o saldo correto de
                                horas extras e faltas. Além disso, funcionários
                                que não controlam banco de horas não são
                                listados nesta tela.{' '}
                                {hasPermission(['read', 'Funcionários'], () => (
                                    <Link href="/cadastros/funcionarios">
                                        Alterar configuração
                                    </Link>
                                ))}
                            </FooterAviso>
                        ),
                    }}
                />
            </ContentGuide.Card>
            <Outlet
                context={{
                    data,
                    competencia,
                    selecionados,
                }}
            />
        </ContentGuide>
    )
}

function Toolbar() {
    return (
        <>
            <ExportarCsv fileName="exportacao-pagamento-horas" />
            {hasPermission(['read', 'Rubricas']) && (
                <GridMenuButton
                    href="/cadastros/rubricas"
                    startIcon={<SettingsIcon />}
                    label="Configurar"
                />
            )}
        </>
    )
}

export function usePagamentoHorasOutletContext() {
    return useOutletContext<{
        data?: {
            pagamento: PagamentoHoras
            limite: Partial<SaldoHoras>
            saldo: SaldoHoras
        }[]
        competencia?: string
        selecionados?: GridSelectionModel
    }>()
}
